import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VerificationService } from '../../services/verificationService';
import { VerificationType } from '../../types/task';
import BasePageLayout from '../../components/BasePageLayout';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useLanguage } from '../../context/LanguageContext';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import { ContentLanguageMetadataService } from '../../services/contentLanguageMetadataService';
import { supabase } from '../../services/supabaseClient';
import '../../styles/pages/CreateTaskPage.css';

const CreateTaskPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [points, setPoints] = useState(100);
  const [instructions, setInstructions] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [verificationType, setVerificationType] = useState<VerificationType>('email_username');
  const [textResponseQuestion, setTextResponseQuestion] = useState('');
  const [maxParticipants, setMaxParticipants] = useState<number | undefined>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [currentStep, setCurrentStep] = useState(1);

  // Track if user has interacted with task cost fields
  const [hasSetTaskCost, setHasSetTaskCost] = useState(false);

  // Liability tracking state
  const [liabilityInfo, setLiabilityInfo] = useState<{
    existingLiability: number;
    currentBalance: number;
    fundingStatus: string;
  } | null>(null);
  const [loadingLiability, setLoadingLiability] = useState(false);

  const { user } = useAuth();
  const { selectedCountry } = useCountry();
  const { contentLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const { deviceType } = useDeviceType();

  // Form validation state
  const [isBasicComplete, setIsBasicComplete] = useState(false);
  const [isDetailsComplete, setIsDetailsComplete] = useState(false);
  const [isVerificationComplete, setIsVerificationComplete] = useState(true);

  // Load template data if coming from journey workflow
  useEffect(() => {
    const loadTemplate = async () => {
      const templateId = searchParams.get('template');
      const stepId = searchParams.get('stepId');
      const journeyId = searchParams.get('journeyId');

      if (stepId && journeyId) {
        try {
          const integrationData = await JourneyPlatformIntegrationService.prepareIntegrationData(
            stepId,
            'task',
            templateId || undefined, // Allow undefined for contextual matching
            journeyId
          );

          // Only extract template data if we have a template
          if (integrationData.template) {
            const templateData = JourneyPlatformIntegrationService.extractTemplateData(integrationData.template);

            // Pre-fill form with template data
            if (templateData.title) setTitle(templateData.title);
            if (templateData.description) setDescription(templateData.description);
            if (templateData.instructions) setInstructions(templateData.instructions);
            if (templateData.points) setPoints(templateData.points);
            if (templateData.category) setCategory(templateData.category);
            if (templateData.tags && Array.isArray(templateData.tags)) setTags(templateData.tags);
            if (templateData.verificationType) setVerificationType(templateData.verificationType);
            if (templateData.textResponseQuestion) setTextResponseQuestion(templateData.textResponseQuestion);
            if (templateData.maxParticipants) setMaxParticipants(templateData.maxParticipants);
          }
        } catch (err) {
          console.error('Failed to load template:', err);
          setError('Failed to load template data. You can still create your task manually.');
        }
      }
    };

    loadTemplate();
  }, [searchParams]);

  // Validate form sections
  useEffect(() => {
    setIsBasicComplete(title.trim() !== '' && description.trim() !== '' && instructions.trim() !== '');
    setIsDetailsComplete(category !== '' && points > 0);
    setIsVerificationComplete(
      verificationType !== 'text_response' || textResponseQuestion.trim() !== ''
    );
  }, [title, description, instructions, category, points, verificationType, textResponseQuestion]);

  // Auto-advance to next tab when section is complete
  useEffect(() => {
    if (activeTab === 'basic' && isBasicComplete) {
      setCurrentStep(2);
    } else if (activeTab === 'details' && isDetailsComplete) {
      setCurrentStep(3);
    } else if (activeTab === 'verification' && isVerificationComplete) {
      setCurrentStep(4);
    }
  }, [activeTab, isBasicComplete, isDetailsComplete, isVerificationComplete]);

  // Fetch publisher liability info
  useEffect(() => {
    const fetchLiability = async () => {
      if (!user?.id) return;

      setLoadingLiability(true);
      try {
        const { data, error } = await supabase.rpc('calculate_publisher_liability', {
          p_publisher_id: user.id
        });

        if (error) {
          console.error('Error fetching liability:', error);
          return;
        }

        if (data && data.length > 0) {
          setLiabilityInfo({
            existingLiability: data[0].total_liability || 0,
            currentBalance: data[0].current_balance || 0,
            fundingStatus: data[0].funding_status || 'UNKNOWN'
          });
        }
      } catch (err) {
        console.error('Error calculating liability:', err);
      } finally {
        setLoadingLiability(false);
      }
    };

    fetchLiability();
  }, [user?.id]);

  // Handle tag operations
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const addTag = () => {
    const trimmedInput = tagInput.trim();
    if (trimmedInput && !tags.includes(trimmedInput) && tags.length < 10) {
      setTags([...tags, trimmedInput]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Calculate total cost
  const totalCost = maxParticipants && maxParticipants > 0 ? points * maxParticipants : points;
  const hasInsufficientPoints = totalCost > (user?.points || 0);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !isBasicComplete || !isDetailsComplete || !isVerificationComplete) return;

    try {
      setLoading(true);
      setError(null);

      // NEW: Check total liability before creating task (fairness check)
      const { data: validationData, error: validationError } = await supabase.rpc('can_create_task', {
        p_publisher_id: user.id,
        p_task_points: points,
        p_max_participants: maxParticipants || null
      });

      if (validationError) {
        console.error('Validation error:', validationError);
        throw new Error('Failed to validate task creation. Please try again.');
      }

      if (validationData && validationData.length > 0) {
        const validation = validationData[0];

        if (!validation.can_create) {
          // Show detailed error about why task cannot be created
          throw new Error(validation.reason || 'Insufficient points to create this task.');
        }

        // Optional: Show warning if balance will be low after creation
        if (validation.current_balance - validation.total_required < points) {
          const proceed = window.confirm(
            `Warning: After creating this task, you'll have ${validation.current_balance - validation.total_required} points remaining. ` +
            `This may not be enough to create another task. Continue?`
          );

          if (!proceed) {
            setLoading(false);
            return;
          }
        }
      }

      // Legacy check (kept for backward compatibility, but above check is more comprehensive)
      if (hasInsufficientPoints) {
        throw new Error(`Insufficient points. You need ${totalCost} points but only have ${user.points}.`);
      }

      const verification_details = await VerificationService.generateVerificationDetails(
        verificationType,
        verificationType === 'text_response' ? textResponseQuestion : undefined
      );

      const taskData = {
        title,
        description,
        points,
        instructions,
        category,
        tags,
        created_by: user.id,
        verification_type: verificationType,
        verification_details,
        max_participants: maxParticipants,
        country_code: selectedCountry,
      };

      const { task, error } = await VerificationService.createTask(taskData);

      if (error) throw error;
      if (!task) throw new Error('Failed to create task');

      // Store language metadata in localStorage
      ContentLanguageMetadataService.setContentLanguage(
        'task',
        task.id,
        contentLanguage as 'en' | 'zh'
      );

      // Track completion if this came from a journey workflow
      const stepId = searchParams.get('stepId');
      if (stepId && task.id) {
        try {
          await JourneyPlatformIntegrationService.trackActionCompletion(
            stepId,
            'task',
            task.id
          );
        } catch (trackingError) {
          console.error('Failed to track action completion:', trackingError);
          // Don't fail the whole operation if tracking fails
        }
      }

      navigate(`/tasks/${task.id}`)
    } catch (err) {
      console.error('Error creating task:', err);
      setError(err instanceof Error ? err.message : 'Failed to create task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tabNavigation = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'basic') setCurrentStep(1);
    else if (tab === 'details') setCurrentStep(2);
    else if (tab === 'verification') setCurrentStep(3);
  };

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="create-task-page">
        {/* Form Container */}
        <div className="form-container">
          <div className="create-task-card">
            {/* Header with Title and Progress */}
            <div className="create-task-header">
              <div className="create-task-title-section">
                <h1 className="create-task-title">
                  <i className="bi bi-plus-circle"></i>
                  Task Creation
                </h1>
              </div>
              <div className="step-progress">
                <div
                  className={`step-item ${currentStep >= 1 ? 'active' : ''} ${isBasicComplete ? 'completed' : ''}`}
                  onClick={() => tabNavigation('basic')}
                >
                  <div className="step-number">1</div>
                  <div className="step-label">{t('createTask.stepBasicInfo')}</div>
                </div>
                <div className="step-connector"></div>
                <div
                  className={`step-item ${currentStep >= 2 ? 'active' : ''} ${isDetailsComplete ? 'completed' : ''}`}
                  onClick={() => tabNavigation('details')}
                >
                  <div className="step-number">2</div>
                  <div className="step-label">{t('createTask.stepTaskDetails')}</div>
                </div>
                <div className="step-connector"></div>
                <div
                  className={`step-item ${currentStep >= 3 ? 'active' : ''} ${isVerificationComplete ? 'completed' : ''}`}
                  onClick={() => tabNavigation('verification')}
                >
                  <div className="step-number">3</div>
                  <div className="step-label">{t('createTask.stepVerification')}</div>
                </div>              </div>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSubmit} className="form-content">
              <div className="tab-content">
                {error && (
                  <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle"></i>
                    {error}
                  </div>
                )}

                {/* Liability Warning - Show publisher's financial status */}
                {/* Only show after user has explicitly set task cost (points or max participants) */}
                {liabilityInfo && hasSetTaskCost && points > 0 && (
                  <div className={`liability-warning ${liabilityInfo.fundingStatus === 'FULLY_FUNDED' ? 'alert-info' :
                    liabilityInfo.fundingStatus === 'UNDER_FUNDED' ? 'alert-warning' :
                      liabilityInfo.fundingStatus === 'BROKE' ? 'alert-danger' :
                        'alert-info'
                    }`}>
                    <div className="liability-header">
                      <i className={`bi ${liabilityInfo.fundingStatus === 'FULLY_FUNDED' ? 'bi-check-circle' :
                        liabilityInfo.fundingStatus === 'NO_LIABILITY' ? 'bi-info-circle' :
                          'bi-exclamation-triangle'
                        }`}></i>
                      <strong>{t('tasks.create.pointsSummary.title')}</strong>
                    </div>
                    <div className="liability-details">
                      <div className="liability-row">
                        <span>{t('tasks.create.pointsSummary.yourBalance')}</span>
                        <strong>{user?.points || 0} {t('tasks.create.pointsSummary.points')}</strong>
                      </div>
                      <div className="liability-row">
                        <span>{t('tasks.create.pointsSummary.reservedForExisting')}</span>
                        <span>{liabilityInfo.existingLiability} {t('tasks.create.pointsSummary.points')}</span>
                      </div>
                      <div className="liability-row">
                        <span>{t('tasks.create.pointsSummary.newTaskCost')}</span>
                        <span className="highlight">
                          {totalCost} {t('tasks.create.pointsSummary.points')}
                          {maxParticipants && maxParticipants > 0 && (
                            <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '0.5rem' }}>
                              ({points} × {maxParticipants} {maxParticipants === 1 ? t('tasks.create.pointsSummary.participant') : t('tasks.create.pointsSummary.participants')})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="liability-row total">
                        <span>{t('tasks.create.pointsSummary.totalRequired')}</span>
                        <strong>{liabilityInfo.existingLiability + totalCost} {t('tasks.create.pointsSummary.points')}</strong>
                      </div>
                      <div className="liability-row remaining">
                        <span>{t('tasks.create.pointsSummary.remainingAfter')}</span>
                        <strong className={
                          (user?.points || 0) - (liabilityInfo.existingLiability + totalCost) < 0
                            ? 'text-danger'
                            : (user?.points || 0) - (liabilityInfo.existingLiability + totalCost) < totalCost
                              ? 'text-warning'
                              : 'text-success'
                        }>
                          {(user?.points || 0) - (liabilityInfo.existingLiability + totalCost)} {t('tasks.create.pointsSummary.points')}
                        </strong>
                      </div>
                    </div>
                    {(user?.points || 0) < (liabilityInfo.existingLiability + totalCost) && (
                      <div className="liability-error">
                        <i className="bi bi-x-circle"></i>
                        <strong>{t('tasks.create.pointsSummary.cannotCreate')}</strong> {t('tasks.create.pointsSummary.needMore', {
                          points: (liabilityInfo.existingLiability + totalCost) - (user?.points || 0)
                        })} <a href="/claim-bonuses">{t('tasks.create.pointsSummary.getMore')}</a>.
                      </div>
                    )}
                    {liabilityInfo.existingLiability > 0 && (user?.points || 0) >= (liabilityInfo.existingLiability + totalCost) && (
                      <div className="liability-info">
                        <i className="bi bi-info-circle"></i>
                        {t('tasks.create.pointsSummary.pendingTasks')}
                      </div>
                    )}
                  </div>
                )}

                {/* Basic Information Tab */}
                <div className={`tab-pane ${activeTab === 'basic' ? 'active' : ''}`}>
                  <div className="form-section">

                    <div className="form-grid form-grid-2col">
                      <div className="form-group">
                        <label htmlFor="title" className="form-label">
                          {t('createTask.taskTitleLabel')} <span className="required">{t('createTask.required')}</span>
                        </label>
                        <input
                          type="text"
                          id="title"
                          className="form-control"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          disabled={loading}
                          required
                          placeholder={t('createTask.taskTitlePlaceholder')}
                        />
                        <div className="form-help">
                          {t('createTask.taskTitleHelp')}
                        </div>
                      </div>

                      <div className="form-group">
                        <label htmlFor="category" className="form-label">
                          {t('createTask.categoryLabel')} <span className="required">{t('createTask.required')}</span>
                        </label>
                        <select
                          id="category"
                          className="form-select"
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          disabled={loading}
                          required
                        >
                          <option value="" disabled>{t('tasks.selectCategory')}</option>
                          <option value="Mobile App">{t('category.mobileApp')}</option>
                          <option value="Website">{t('category.website')}</option>
                          <option value="Desktop App">{t('category.desktopApp')}</option>
                          <option value="Game">{t('category.game')}</option>
                          <option value="SaaS">{t('category.saas')}</option>
                          <option value="Other">{t('category.other')}</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="description" className="form-label">
                        {t('createTask.descriptionLabel')} <span className="required">{t('createTask.required')}</span>
                      </label>
                      <textarea
                        id="description"
                        className="form-control"
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={loading}
                        required
                        placeholder={t('createTask.descriptionPlaceholder')}
                      />
                      <div className="form-help">
                        {t('createTask.descriptionHelp')}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="instructions" className="form-label">
                        {t('createTask.instructionsLabel')} <span className="required">{t('createTask.required')}</span>
                      </label>
                      <textarea
                        id="instructions"
                        className="form-control"
                        rows={5}
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        disabled={loading}
                        required
                        placeholder={t('createTask.instructionsPlaceholder')}
                      />
                      <div className="form-help">
                        {t('createTask.instructionsHelp')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Task Details Tab */}
                <div className={`tab-pane ${activeTab === 'details' ? 'active' : ''}`}>
                  <div className="form-section">
                    <h2 className="section-title">
                      <i className="bi bi-sliders section-icon"></i>
                      {t('createTask.taskConfiguration')}
                    </h2>

                    <div className="form-grid form-grid-3col">
                      <div className="form-group">
                        <label htmlFor="points" className="form-label">
                          {t('createTask.pointsRewardLabel')} <span className="required">{t('createTask.required')}</span>
                          <i className="bi bi-question-circle help-icon" title={t('createTask.pointsRewardTooltip')}></i>
                        </label>
                        <input
                          type="number"
                          id="points"
                          className="form-control"
                          value={points}
                          onChange={(e) => {
                            setPoints(parseInt(e.target.value) || 0);
                            setHasSetTaskCost(true); // Mark that user has interacted with cost fields
                          }}
                          min="1"
                          max="1000"
                          disabled={loading}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="maxParticipants" className="form-label">
                          {t('createTask.maxParticipantsLabel')}
                          <i className="bi bi-question-circle help-icon" title={t('createTask.maxParticipantsTooltip')}></i>
                        </label>
                        <input
                          type="number"
                          id="maxParticipants"
                          className="form-control"
                          value={maxParticipants || ''}
                          onChange={(e) => {
                            const value = e.target.value ? parseInt(e.target.value) : undefined;
                            setMaxParticipants(value === 0 ? undefined : value);
                            setHasSetTaskCost(true); // Mark that user has interacted with cost fields
                          }}
                          min="0"
                          max="10000"
                          disabled={loading}
                          placeholder={t('createTask.maxParticipantsPlaceholder')}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">{t('createTask.pointsSummary')}</label>
                        <div className={`points-display ${hasInsufficientPoints ? 'insufficient-points' : ''}`}>
                          <div className="points-row">
                            <span className="points-label">{t('createTask.pointsAvailable')}</span>
                            <span className="points-value">{user?.points || 0}</span>
                          </div>
                          <div className="points-row points-cost-row">
                            <span className="points-label">{t('createTask.pointsRequired')}</span>
                            <span className="points-value points-cost">{totalCost}</span>
                          </div>
                        </div>
                        {hasInsufficientPoints && (
                          <div className="form-warning">
                            <i className="bi bi-exclamation-triangle"></i>
                            {t('createTask.insufficientPoints', { amount: totalCost - (user?.points || 0) })}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('createTask.tagsLabel')}
                        <i className="bi bi-question-circle help-icon" title={t('createTask.tagsTooltip')}></i>
                      </label>
                      <div className="tags-container">
                        {tags.map((tag, index) => (
                          <div key={index} className="tag-item">
                            {tag}
                            <button
                              type="button"
                              className="tag-remove"
                              onClick={() => removeTag(tag)}
                              title={t('createTask.removeTag')}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        <input
                          type="text"
                          className="tag-input"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagKeyDown}
                          onBlur={addTag}
                          disabled={loading || tags.length >= 10}
                          placeholder={tags.length === 0 ? t('createTask.tagsPlaceholder') : tags.length >= 10 ? t('createTask.tagsMaxReached') : ""}
                        />
                      </div>
                      <div className="form-help">
                        {t('createTask.tagsHelp')}
                      </div>
                    </div>

                    {maxParticipants && maxParticipants > 0 && (
                      <div className="form-info">
                        <i className="bi bi-info-circle"></i>
                        {t('createTask.limitedParticipantsInfo', {
                          cost: totalCost,
                          count: maxParticipants,
                          points: points
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Verification Method Tab */}
                <div className={`tab-pane ${activeTab === 'verification' ? 'active' : ''}`}>
                  <div className="form-section">

                    <div className="verification-grid">
                      {/* Email/Username Verification */}
                      <div
                        className={`verification-card ${verificationType === 'email_username' ? 'selected' : ''}`}
                        onClick={() => setVerificationType('email_username')}
                      >
                        <div className="verification-header">
                          <i className="bi bi-envelope verification-icon"></i>
                          <div className="verification-title">{t('createTask.emailUsernameTitle')}</div>
                        </div>
                        <div className="verification-description">
                          {t('createTask.emailUsernameDescription')}
                        </div>
                        {verificationType === 'email_username' && (
                          <div className="verification-details">
                            <strong>{t('createTask.howItWorks')}</strong>
                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                              <li>{t('createTask.emailUsernameStep1')}</li>
                              <li>{t('createTask.emailUsernameStep2')}</li>
                              <li>{t('createTask.emailUsernameStep3')}</li>
                              <li>{t('createTask.emailUsernameStep4')}</li>
                            </ul>
                          </div>
                        )}
                      </div>

                      {/* Text Response Verification */}
                      <div
                        className={`verification-card ${verificationType === 'text_response' ? 'selected' : ''}`}
                        onClick={() => setVerificationType('text_response')}
                      >
                        <div className="verification-header">
                          <i className="bi bi-chat-text verification-icon"></i>
                          <div className="verification-title">{t('createTask.textResponseTitle')}</div>
                        </div>
                        <div className="verification-description">
                          {t('createTask.textResponseDescription')}
                        </div>
                        {verificationType === 'text_response' && (
                          <div className="verification-details">
                            <div className="form-group" style={{ margin: '1rem 0 0 0' }}>
                              <label htmlFor="textResponseQuestion" className="form-label">
                                {t('createTask.verificationQuestionLabel')} <span className="required">{t('createTask.required')}</span>
                              </label>
                              <input
                                type="text"
                                id="textResponseQuestion"
                                className="form-control"
                                value={textResponseQuestion}
                                onChange={(e) => setTextResponseQuestion(e.target.value)}
                                disabled={loading}
                                required={verificationType === 'text_response'}
                                placeholder={t('createTask.verificationQuestionPlaceholder')}
                              />
                              <div className="form-help">
                                {t('createTask.verificationQuestionHelp')}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Screenshot Verification */}
                      <div
                        className={`verification-card ${verificationType === 'screenshot' ? 'selected' : ''}`}
                        onClick={() => setVerificationType('screenshot')}
                      >
                        <div className="verification-header">
                          <i className="bi bi-image verification-icon"></i>
                          <div className="verification-title">{t('createTask.screenshotTitle')}</div>
                        </div>
                        <div className="verification-description">
                          {t('createTask.screenshotDescription')}
                        </div>
                        {verificationType === 'screenshot' && (
                          <div className="verification-details">
                            <strong>{t('createTask.howItWorks')}</strong>
                            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.25rem' }}>
                              <li>{t('createTask.screenshotStep1')}</li>
                              <li>{t('createTask.screenshotStep2')}</li>
                              <li>{t('createTask.screenshotStep3')}</li>
                              <li>{t('createTask.screenshotStep4')}</li>
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Form Actions */}
              <div className="form-actions">
                <div>
                  {activeTab !== 'basic' && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        if (activeTab === 'details') tabNavigation('basic');
                        else if (activeTab === 'verification') tabNavigation('details');
                      }}
                    >
                      <i className="bi bi-arrow-left"></i>
                      {t('createTask.previousButton')}
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  {activeTab !== 'verification' ? (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        if (activeTab === 'basic' && isBasicComplete) tabNavigation('details');
                        else if (activeTab === 'details' && isDetailsComplete) tabNavigation('verification');
                      }}
                      disabled={
                        (activeTab === 'basic' && !isBasicComplete) ||
                        (activeTab === 'details' && (!isDetailsComplete || hasInsufficientPoints))
                      }
                    >
                      {t('createTask.nextStepButton')}
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading || !isBasicComplete || !isDetailsComplete || !isVerificationComplete || hasInsufficientPoints}
                    >
                      {loading ? (
                        <>
                          <i className="bi bi-hourglass-split"></i>
                          {t('createTask.creatingTask')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-plus-circle"></i>
                          {t('createTask.createTaskButton')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default CreateTaskPage;
